// Widget configuration shared by the console (editor + live preview), the API
// (stored per widget) and the widget bundle that runs on installers' websites.
// Keep this file free of DOM and framework code so every side can import it.

/** The screens a homeowner moves through, in order. */
export type Step = "postcode" | "address" | "details" | "quote" | "contact" | "done";
export const STEPS: Step[] = ["postcode", "address", "details", "quote", "contact", "done"];
export const STEP_LABELS: Record<Step, string> = {
  postcode: "Postcode",
  address: "Address",
  details: "Property",
  quote: "Quote",
  contact: "Contact",
  done: "Thank you",
};

export type ShadowLevel = "none" | "soft" | "strong";
export type LayoutMode = "inline" | "floating";
/**
 * small  = the compact step-by-step card
 * full   = the large page with property details, grant check and sidebar, on the installer's site
 * hosted = the full widget on a page we host (go.xenquote.com/<link>) with the
 *          installer's own header and footer
 * contact = a contact form in the compact card's look: emails the enquiry to the
 *          installer (optional address look-up and "what's it about" drop-down)
 */
export type WidgetStyle = "small" | "full" | "hosted" | "contact";

/** The contact form's own options (contact style only). */
export interface ContactFormOptions {
  askAddress: boolean; // postcode look-up + choose the house; required when on
  topicEnabled: boolean; // a "what is your enquiry about?" drop-down
  topicLabel: string;
  topics: string[]; // the drop-down's choices, in order
  topicRequired: boolean;
}

export const DEFAULT_CONTACT_FORM: ContactFormOptions = {
  askAddress: false,
  topicEnabled: false,
  topicLabel: "What is your enquiry about?",
  topics: ["Air source heat pump", "Servicing or repair", "Solar panels", "Something else"],
  topicRequired: true,
};

/** The installer's details shown in a hosted page's header and footer. */
export interface HostedDetails {
  companyName: string;
  tagline: string;
  phone: string;
  email: string;
  website: string; // https://…
  address: string; // one line
  companyNumber: string;
  vatNumber: string;
  privacyUrl: string; // https://…
  logoUrl: string | null; // header logo (uploaded in the console); null = the calculator's logo
  theme: HostedTheme | null; // null = colours and font from the calculator's branding
}

/** The hosted page's own look (header, page and footer), separate from the calculator's. */
export interface HostedTheme {
  font: string; // Google Fonts family
  headerBackground: string;
  headerText: string;
  accent: string; // header rule and call button
  pageBackground: string;
  footerBackground: string;
  footerText: string;
}

/** The hosted page's look, falling back to the calculator's branding where unset. */
export function hostedTheme(h: Partial<HostedDetails> | null | undefined, b: WidgetBranding): HostedTheme {
  return (
    h?.theme ?? {
      font: b.font.family,
      headerBackground: b.colors.background,
      headerText: b.colors.text,
      accent: b.colors.primary,
      pageBackground: "#F4F5F7",
      footerBackground: b.colors.text,
      footerText: "#FFFFFF",
    }
  );
}

export const EMPTY_HOSTED: HostedDetails = {
  companyName: "",
  tagline: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  companyNumber: "",
  vatNumber: "",
  privacyUrl: "",
  logoUrl: null,
  theme: null,
};
export type InlineAlign = "left" | "center" | "right";
export type FloatingPosition = "bottom-right" | "bottom-left";

/** Everything the installer can customise about how a widget looks. */
export interface WidgetBranding {
  font: {
    family: string; // a Google Fonts family name (see fonts.ts)
  };
  colors: {
    primary: string; // buttons, progress bar, selected options
    primaryText: string; // text on primary
    background: string; // widget canvas
    text: string; // headings and body text
    mutedText: string; // hints and secondary text
    border: string; // input and card borders
    inputBackground: string;
    accent: string; // quote highlight
  };
  shape: {
    radius: number; // canvas corner radius, px
    buttonRadius: number; // buttons and inputs, px
    shadow: ShadowLevel;
    border: boolean; // outline around the canvas
  };
  layout: {
    style: WidgetStyle; // small card or full quote page
    mode: LayoutMode; // inline = sits in the page; floating = launcher button + panel
    width: number; // canvas width, px (inline: max width when fullWidth)
    fullWidth: boolean; // inline only: stretch to the container
    height: number | null; // fixed canvas height, px; null = grow with content
    padding: number; // inner spacing, px
    align: InlineAlign; // inline only
    position: FloatingPosition; // floating only
    offsetX: number; // floating only: distance from the side, px
    offsetY: number; // floating only: distance from the bottom, px
    launcherLabel: string; // floating only: text on the launcher button
  };
  copy: {
    title: string;
    subtitle: string;
    buttonText: string; // postcode step call to action
    quoteTitle: string;
    disclaimer: string;
    consentText: string; // GDPR consent next to the contact form checkbox
    thankYouTitle: string;
    thankYouText: string;
  };
  logoUrl: string | null; // optional logo shown at the top of the canvas
  /** Contact form components only: which fields the form has */
  form: ContactFormOptions;
}

export const DEFAULT_BRANDING: WidgetBranding = {
  font: { family: "Inter" },
  colors: {
    primary: "#202937", // xenscope navy
    primaryText: "#FFFFFF",
    background: "#FFFFFF",
    text: "#202937",
    mutedText: "#5F6670",
    border: "#D9DCE1",
    inputBackground: "#FFFFFF",
    accent: "#E1EE2B",
  },
  shape: { radius: 12, buttonRadius: 8, shadow: "soft", border: true },
  layout: {
    style: "small",
    mode: "inline",
    width: 480,
    fullWidth: false,
    height: null,
    padding: 28,
    align: "center",
    position: "bottom-right",
    offsetX: 24,
    offsetY: 24,
    launcherLabel: "Get a heat pump quote",
  },
  copy: {
    title: "How much would a heat pump cost?",
    subtitle: "Enter your postcode for an instant estimate for your home.",
    buttonText: "Find my address",
    quoteTitle: "Your estimated cost",
    disclaimer: "An estimate only. We'll confirm your price after a home survey.",
    consentText: "I agree to be contacted about my quote. We never share your details.",
    thankYouTitle: "Thanks, we'll be in touch",
    thankYouText: "One of our team will contact you shortly to arrange a home survey.",
  },
  logoUrl: null,
  form: DEFAULT_CONTACT_FORM,
};

/** A new contact form's wording and launcher (its style's defaults). */
export const CONTACT_COPY: WidgetBranding["copy"] = {
  title: "Get in touch",
  subtitle: "Send us a message and we'll get back to you.",
  buttonText: "Send message",
  quoteTitle: "",
  disclaimer: "",
  consentText: "I agree to be contacted about my enquiry. We never share your details.",
  thankYouTitle: "Thanks, we've got your message",
  thankYouText: "One of our team will get back to you shortly.",
};

/** Screens of the contact form (the console preview's step bar). */
export type ContactStep = "form" | "done";
export const CONTACT_STEPS: [ContactStep, string][] = [
  ["form", "Form"],
  ["done", "Thank you"],
];

/** What the contact form sends. The API re-checks it against the form's options. */
export interface ContactEnquiry {
  name: string;
  email: string;
  phone: string;
  message: string;
  topic: string | null;
  address: Address | null;
  consent: boolean;
}

// Ranges enforced by the editor and the API
export const LIMITS = {
  radius: [0, 32],
  buttonRadius: [0, 32],
  width: [300, 900],
  height: [360, 1000],
  padding: [12, 48],
  offset: [0, 120],
} as const;

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

/** Fill any missing fields from the defaults (older saved widgets, partial input). */
export function normaliseBranding(input?: DeepPartial<WidgetBranding> | null): WidgetBranding {
  const d = DEFAULT_BRANDING;
  const i = input ?? {};
  return {
    font: { ...d.font, ...i.font },
    colors: { ...d.colors, ...i.colors },
    shape: { ...d.shape, ...i.shape },
    layout: { ...d.layout, ...i.layout },
    copy: { ...d.copy, ...i.copy },
    logoUrl: i.logoUrl === undefined ? d.logoUrl : (i.logoUrl ?? null),
    form: { ...d.form, ...i.form, topics: (i.form?.topics as string[] | undefined) ?? d.form.topics },
  } as WidgetBranding;
}

// ─── Data the widget exchanges with the API ──────────────────────────────────

export interface Address {
  id: string; // UPRN
  line: string; // single-line display address
}

export interface PropertyDetails {
  propertyType: "detached" | "semi-detached" | "terraced" | "flat" | "bungalow";
  bedrooms: number;
  heating: "gas" | "oil" | "lpg" | "electric" | "other";
}

/** One line of the heat loss breakdown (an element's W/K at the design ΔT). */
export interface HeatLossElement {
  element: string; // "External walls", "Roof", "Windows", "Ventilation", …
  area?: number; // m² (fabric elements)
  u?: number; // W/m²K
  wPerK: number; // heat loss coefficient
  source?: string; // published table the U-value came from
}

/** The estimated design heat loss and how it was reached (Full widget). */
export interface HeatLoss {
  kw: number;
  level: "A" | "B" | "C" | "D"; // docs/03: A/B from this home's EPC, C/D from similar homes
  internalC: number;
  externalC: number;
  designLocation: string; // MIS 3005-D Table 2 location used
  elements: HeatLossElement[];
  totalWPerK: number;
  method: string; // e.g. "RdSAP 10 defaults applied to your EPC and answers"
}

export interface Quote {
  low: number; // £, after grant
  high: number;
  heatPumpKw: number;
  grant: number; // £ Boiler Upgrade Scheme deduction applied
  price?: number; // £ installed price of the chosen size, before the grant
  requiredKw?: number; // estimated heat loss the size was chosen for
  // True when no size the installer offers covers the home: no price is shown
  // (low/high are 0 and heatPumpKw is the largest size offered)
  tooBig?: boolean;
  heatLoss?: HeatLoss; // Full widget: the published-method breakdown
  noEstimate?: boolean; // Full widget: the method can't estimate this home online (survey instead)
}

export interface Lead {
  address: Address;
  details: PropertyDetails;
  quote: Quote;
  full?: FullQuoteRequest; // Full widget: every Property answer and the grant applied
  name: string;
  email: string;
  phone: string;
  consent: boolean;
}

/**
 * What the home's EPC (energy performance certificate) says, already mapped to
 * the widget's options by the API:
 *   propertyType ← EPC property-type + built-form (e.g. House + Semi-Detached → semi-detached)
 *   bedrooms     ← EPC number-habitable-rooms minus one living room, clamped to 1–5
 *   heating      ← EPC main-fuel (mains gas → gas, oil → oil, LPG → lpg, electricity → electric)
 * Any field the certificate doesn't give is omitted.
 */
export interface PropertyLookup {
  details: Partial<PropertyDetails>;
  epcRating?: string; // current energy rating, A–G
  floorArea?: number; // m²
  full?: FullLookup; // Full widget: the EPC card and Property page pre-fill
}

// ─── Full widget ─────────────────────────────────────────────────────────────

/** Steps of the Full widget, in order. */
export type FullStep = "location" | "epc" | "property" | "grant" | "estimate" | "contact" | "done";
export const FULL_STEPS: FullStep[] = ["location", "epc", "property", "grant", "estimate", "contact", "done"];

/** The EPC card: address, certificate number and current/potential ratings. */
export interface EpcCertificate {
  number: string | null;
  lodgedDate: string | null;
  inspectionDate: string | null;
  addressLines: string[];
  country: string | null;
  current: { score: number | null; band: string | null };
  potential: { score: number | null; band: string | null };
}

/** The Property page's answers (pre-filled from the EPC, editable by the homeowner). */
export interface FullProperty {
  kind: "house" | "bungalow" | "flat" | "maisonette" | "park-home";
  builtForm: "detached" | "semi-detached" | "end-terrace" | "mid-terrace" | "enclosed-end-terrace" | "enclosed-mid-terrace";
  ageBand: string; // RdSAP 10 Table 1 age band, A–M
  storeys: number;
  roomInRoof: boolean;
  floorArea: number; // m²
  fuel: "gas" | "oil" | "lpg" | "electric" | "other";
  heatingSystem: "boiler-radiators" | "boiler-underfloor" | "storage-heaters" | "room-heaters" | "heat-pump" | "other";
  hotWater: "main-system" | "immersion" | "other";
  bedrooms: number;
  bathrooms: number;
  radiators: number;
  wallsInsulated: boolean;
  roofInsulated: boolean | null; // null = not applicable (another home above)
  floorInsulated: boolean | null; // null = not applicable (another home below)
  mainsGas: boolean | null;
}

export interface FullLookup {
  certificate: EpcCertificate;
  property: Partial<FullProperty>;
  country: string | null;
}

/** The homeowner's answers on the grant step. */
export interface GrantAnswers {
  owner: boolean;
  newBuild: boolean;
  previousFunding: boolean;
  mainsGas: boolean | null; // asked for oil/LPG homes (the uplift is for off-gas homes)
}

export interface GrantCheck {
  key: string;
  text: string;
  status: "pass" | "fail" | "info";
  detail: string;
}

/** Boiler Upgrade Scheme guide (Ofgem Property Owner Guidance V5.1). */
export interface GrantResult {
  grant: number; // £ this home looks eligible for (0 if not)
  uplift: boolean; // the £9,000 off-gas oil/LPG uplift applies
  eligible: boolean;
  checks: GrantCheck[];
  note: string;
}

/** What the Full widget sends for a quote. The API re-checks the grant from the
 *  answers itself, so a page can't award its own grant. */
export interface FullQuoteRequest {
  property: FullProperty;
  grantAnswers: GrantAnswers;
  includeGrant: boolean; // the homeowner chose to see prices with the grant taken off
}

/** Backend the widget talks to: the real API on websites, a mock in the console preview. */
export interface WidgetApi {
  searchAddresses(postcode: string): Promise<Address[]>;
  /** EPC-based details for the chosen address; null when the home has no EPC. */
  lookupProperty(address: Address): Promise<PropertyLookup | null>;
  getQuote(address: Address, details: PropertyDetails, full?: FullQuoteRequest): Promise<Quote>;
  /** Full widget: Boiler Upgrade Scheme checks for this home and the homeowner's answers. */
  checkGrant?(address: Address, property: FullProperty, answers: GrantAnswers): Promise<GrantResult>;
  submitLead(lead: Lead): Promise<void>;
  /**
   * Full width / hosted with quotes on (Quote workflow add-on): ask for a detailed
   * quote. url is the quote page when one was sent; null means the enquiry went to
   * the installer as a normal callback request instead.
   */
  requestQuote?(lead: Lead): Promise<{ url: string | null; reference: string | null }>;
  /** Contact form components: email the enquiry to the installer. */
  submitContact?(enquiry: ContactEnquiry): Promise<void>;
  /** Funnel analytics for stages only the browser sees. Fire-and-forget: never throws. */
  track?(event: "opened" | "contact"): void;
  /** Solar widgets: geocode an address (EPC list entry or manual house + postcode). */
  solarLocate?(req: SolarLocateRequest): Promise<SolarLocation>;
  /** Solar widgets: the roof nearest a point, ready to draw (outline, panels, terrace trim). */
  solarBuilding?(at: LL): Promise<SolarRoof>;
  /** Solar widgets: figures and prices for every panel count, from the kept panels' yields.
   *  Worked out on the server from the installer's prices, which never reach the browser. */
  solarEstimate?(req: SolarEstimateRequest): Promise<SolarEstimate>;
  /** Solar widgets: email the enquiry; the API works the price out again from the bundles. */
  submitSolarLead?(lead: SolarLead): Promise<void>;
}

// ─── Solar estimate (docs/12-solar-roof-scan.md) ─────────────────────────────

/** Which product a widget is. Saved once at creation; older widgets have none and are "ashp". */
export type WidgetProduct = "ashp" | "solar";

/** Steps of the solar widget, in order (noscan replaces result when the roof can't be scanned). */
export type SolarStep = "address" | "confirm" | "scan" | "result" | "noscan" | "contact" | "done";
export const SOLAR_STEPS: SolarStep[] = ["address", "confirm", "scan", "result", "contact", "done"];
export const SOLAR_STEP_LABELS: Record<SolarStep, string> = {
  address: "Address",
  confirm: "Confirm",
  scan: "Scan",
  result: "Result",
  noscan: "No scan",
  contact: "Contact",
  done: "Thank you",
};

/** A point on the map (WGS84 degrees). */
export interface LL {
  lat: number;
  lng: number;
}

/**
 * One inverter step: the inverter fitted to systems up to upToKwp (panel kWp), and its
 * price. Steps are ascending by upToKwp; the largest step sets the biggest system offered.
 */
export interface SolarInverter {
  label: string; // shown to the homeowner, e.g. "3.68 kW inverter"
  upToKwp: number; // systems up to this many kWp of panels get this inverter
  price: number | null; // £ including VAT; null = not offered
}

/**
 * The installer's solar prices (per widget). An installed system costs
 *   basePrice + panels × panelPrice + the inverter for that size (+ the battery)
 * shown to the homeowner as ±10%, rounded to £50. Prices include installation and VAT.
 */
export interface SolarPricing {
  panelWatts: number; // 300–700
  panelModel: string; // e.g. "435 W all-black panels"
  basePrice: number | null; // £ installation: scaffolding, labour, mounting, DNO and MCS paperwork
  panelPrice: number | null; // £ per panel, supplied and fitted
  minPanels: number; // the smallest system offered, in panels (1–60)
  inverters: SolarInverter[]; // 1–8 steps, ascending by upToKwp
  battery: {
    enabled: boolean;
    label: string; // e.g. "5 kWh battery"
    price: number | null; // £ added to the system price
    selfConsumption: number; // 0.5–0.95: share of generation used at home with the battery
  };
}

/**
 * Example prices: the status check still asks the installer to set real ones. Used by
 * the console's price editor only; nothing in the public widget imports it, so it is
 * left out of loader.js (the API applies prices: solar_pricing.py).
 */
export const DEFAULT_SOLAR_PRICING: SolarPricing = {
  panelWatts: 435,
  panelModel: "435 W all-black panels",
  basePrice: 1600,
  panelPrice: 400,
  minPanels: 4,
  inverters: [
    { label: "3.68 kW inverter", upToKwp: 4.4, price: 800 },
    { label: "5.0 kW inverter", upToKwp: 6.0, price: 1050 },
    { label: "6.0 kW inverter", upToKwp: 7.2, price: 1250 },
    { label: "8.0 kW inverter", upToKwp: 9.6, price: 1600 },
  ],
  battery: { enabled: true, label: "5 kWh battery", price: 3200, selfConsumption: 0.8 },
};

/** What the widget sends to geocode an address. */
export interface SolarLocateRequest {
  line?: string; // the EPC address list entry
  uprn?: string;
  house?: string; // manual entry
  postcode: string;
}

/** Where an address is, and whether Google only matched the postcode (approx). */
export interface SolarLocation {
  address: string;
  lat: number;
  lng: number;
  approx: boolean;
}

/**
 * The roof as the widget draws it: everything worked out on the server (outline fitted
 * to the panels, terrace detection and starting trim, each panel's corners). The widget
 * only draws it; none of the method ships in the public bundle. Never stored anywhere.
 */
export interface SolarRoof {
  center: LL;
  imageryQuality: string;
  refWatts: number; // Google's reference panel (sent back with the estimate request)
  outline: Outline;
  fitted: boolean; // the outline was fitted to the panels, so it can be rotated
  terrace: boolean; // looks like a terrace: start in adjust mode with `trim`
  trim: Trim | null; // the starting slice on a terrace
  panels: SolarRoofPanel[]; // every panel the roof holds, best yield first
  view3d: { altitude: number; heading: number } | null; // null = no 3D view for this roof
  summary: string; // "Main roof faces south at about 35°. Aerial data quality: medium."
}

/** One panel position, ready to draw (corners already foreshortened by the roof pitch). */
export interface SolarRoofPanel {
  center: LL;
  corners: LL[];
  kwhDc: number;
}

/** What the widget sends for the result: the yields of the panels kept on the homeowner's roof. */
export interface SolarEstimateRequest {
  kwhDc: number[]; // kept panels, best first
  refWatts: number;
  usageKwh: number;
}

/** One row of the result table: a system of n panels. */
export interface SolarEstimateRow {
  n: number;
  kwp: number;
  inverter: string;
  kwh: number;
  saving: number;
  savingBattery: number;
  coverage: number; // % of usage
  low: number;
  high: number;
  lowBattery: number;
  highBattery: number;
}

/** The result: the panel counts offered and their figures (or tooSmall: show "couldn't scan"). */
export interface SolarEstimate {
  tooSmall?: boolean;
  min: number;
  max: number;
  recommended: number;
  panelModel: string;
  battery: { offered: boolean; label: string };
  packagesNote: string | null;
  rows: SolarEstimateRow[];
}

/** A building outline: centre, long side, short side and the compass heading of the long side. */
export interface Outline {
  c: LL;
  len: number; // m
  wid: number; // m
  heading: number; // degrees
}

/** A trim along the outline's long axis: keep panels whose centre is between t0 and t1 (0–1). */
export interface Trim {
  t0: number;
  t1: number;
}

/** What the solar widget sends with a callback request. */
export interface SolarLead {
  product: "solar";
  address: string; // as the geocoder words it (the email only)
  /** The address as the homeowner chose it (EPC list) or typed it: what the Leads page keeps. */
  line?: string;
  uprn?: string;
  lat: number;
  lng: number;
  name: string;
  email: string;
  phone?: string;
  consent: true;
  /** Missing when the request came from the noscan step. */
  system?: {
    panels: number;
    battery: boolean;
    usageKwh: number;
    /** As shown to the homeowner (the API reports these; it doesn't recompute them). */
    shown: { annualKwh: number; annualSavingGbp: number; roofMaxPanels: number; imageryQuality: string };
  };
}

/** Solar defaults for the installer-editable wording (used when a field is empty). */
export const SOLAR_COPY: WidgetBranding["copy"] = {
  title: "See how many solar panels fit on your roof",
  subtitle: "Enter your address and we'll scan your roof from above. It takes about 10 seconds.",
  buttonText: "Scan my roof",
  quoteTitle: "Your roof fits up to {n} panels",
  disclaimer: "Indicative range including installation and VAT. Your final price is confirmed after a survey.",
  consentText: DEFAULT_BRANDING.copy.consentText,
  thankYouTitle: DEFAULT_BRANDING.copy.thankYouTitle,
  thankYouText: DEFAULT_BRANDING.copy.thankYouText,
};
export const SOLAR_LAUNCHER_LABEL = "See if solar fits your roof";

/** The solar map's height (px) when the installer hasn't set one: tall enough for the
 *  roof and the step card side by side. Installers change it on Branding ("Map height"). */
export const SOLAR_DEFAULT_HEIGHT = 760;
/** The shortest map the step card still fits beside (the stage's CSS min-height). */
export const SOLAR_MIN_HEIGHT = 520;
